import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Footer from '../components/Footer'
import { useUser } from '../contexts/UserContext'
import { ProfileSkeleton } from '../components/LoadingSkeleton'
import toast from 'react-hot-toast'
import { 
  ArrowLeft, 
  Save, 
  X, 
  Plus, 
  Trash2, 
  User, 
  Briefcase, 
  MapPin, 
  Phone, 
  FileText, 
  Award,
  GraduationCap,
  Building,
  Calendar,
  Upload,
  Camera
} from 'lucide-react'
import { motion } from 'framer-motion'

// Fungsi utilitas untuk membersihkan skills
const formatSkill = (skill) =>
  skill.trim().charAt(0).toUpperCase() + skill.trim().slice(1).toLowerCase()

const cleanSkills = (skillsArray) => {
  if (!Array.isArray(skillsArray)) return []
  const formatted = skillsArray.map(formatSkill)
  return [...new Set(formatted)]
}

export default function EditProfile() {
  const { userInfo: cachedUserInfo, loading: userLoading, refreshUserInfo } = useUser()
  const [searchParams] = useSearchParams()
  const userId = searchParams.get('userId') // Get userId from URL if admin is editing another user
  const [userInfo, setUserInfo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [avatarFile, setAvatarFile] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchUser = async () => {
      // Determine which user to fetch: URL param userId (admin editing) or logged-in user
      const targetUserId = userId || cachedUserInfo?.id
      
      if (!targetUserId) {
        return
      }


      setLoadingData(true)
      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', targetUserId)
          .single()

        if (error) {
          console.error('EditProfile - Fetch error:', error)
          toast.error('Gagal memuat data pengguna')
          return
        }


        setUserInfo({
          ...data,
          experience: Array.isArray(data.experience) ? data.experience : [],
          education: Array.isArray(data.education) ? data.education : [],
          skills: Array.isArray(data.skills) ? data.skills : [],
          degree_prefix: data.degree_prefix || '',
          degree_suffix: data.degree_suffix || '',
        })
      } catch (error) {
        console.error('Error loading user data:', error)
        toast.error('Terjadi kesalahan saat memuat data')
      } finally {
        setLoadingData(false)
      }
    }

    if (cachedUserInfo) {
      fetchUser()
    }
  }, [cachedUserInfo, userId])

  const handleChange = (e) => {
    setUserInfo({ ...userInfo, [e.target.name]: e.target.value })
  }

  const handleExperienceChange = (index, field, value) => {
    const updated = [...userInfo.experience]
    updated[index][field] = value
    setUserInfo({ ...userInfo, experience: updated })
  }

  const addExperience = () => {
    setUserInfo({
      ...userInfo,
      experience: [...userInfo.experience, { position: '', company: '', period: '', description: '' }],
    })
  }

  const removeExperience = (index) => {
    const updated = [...userInfo.experience]
    updated.splice(index, 1)
    setUserInfo({ ...userInfo, experience: updated })
  }

  const handleEducationChange = (index, field, value) => {
    const updated = [...userInfo.education]
    updated[index][field] = value
    setUserInfo({ ...userInfo, education: updated })
  }

  const addEducation = () => {
    setUserInfo({
      ...userInfo,
      education: [...userInfo.education, { school: '', degree: '', year: '' }],
    })
  }

  const removeEducation = (index) => {
    const updated = [...userInfo.education]
    updated.splice(index, 1)
    setUserInfo({ ...userInfo, education: updated })
  }

  // Certificate handlers
  const [certificates, setCertificates] = useState([])
  const [certificatesLoading, setCertificatesLoading] = useState(false)

  useEffect(() => {
    if (cachedUserInfo?.id) {
      fetchCertificates()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedUserInfo?.id])

  const fetchCertificates = async () => {
    if (!cachedUserInfo?.id) return
    
    setCertificatesLoading(true)
    try {
      const { data, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('user_id', cachedUserInfo.id)
        .order('issue_date', { ascending: false })

      if (error) throw error
      setCertificates(data || [])
    } catch (error) {
      console.error('Error fetching certificates:', error)
      toast.error('Gagal memuat sertifikat')
    } finally {
      setCertificatesLoading(false)
    }
  }

  const handleCertificateChange = (index, field, value) => {
    const updated = [...certificates]
    updated[index][field] = value
    setCertificates(updated)
  }

  const addCertificate = () => {
    setCertificates([
      ...certificates,
      {
        id: null,
        certificate_name: '',
        issuing_organization: '',
        issue_date: '',
        expiry_date: '',
        credential_id: '',
        credential_url: '',
        description: ''
      }
    ])
  }

  const removeCertificate = async (index) => {
    const cert = certificates[index]
    
    // If certificate has an id, delete from database
    if (cert.id) {
      try {
        const { error } = await supabase
          .from('certificates')
          .delete()
          .eq('id', cert.id)

        if (error) throw error
        toast.success('Sertifikat berhasil dihapus')
      } catch (error) {
        console.error('Error deleting certificate:', error)
        toast.error('Gagal menghapus sertifikat')
        return
      }
    }

    const updated = [...certificates]
    updated.splice(index, 1)
    setCertificates(updated)
  }

  const handleSkillsChange = (e) => {
    const skillsArray = e.target.value.split(',').map((s) => s.trim())
    const cleaned = cleanSkills(skillsArray)
    setUserInfo({ ...userInfo, skills: cleaned })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    let avatarUrl = userInfo.photo

    if (avatarFile) {
      const fileExt = avatarFile.name.split('.').pop()
      const fileName = `${userInfo.id}.${fileExt}`
      const filePath = `avatars/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, avatarFile, { upsert: true })

      if (uploadError) {
        toast.error('Gagal mengunggah foto')
        setLoading(false)
        return
      }

      const { data: publicUrl } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath)

      avatarUrl = publicUrl.publicUrl
    }

    const { error } = await supabase
      .from('users')
      .update({
        full_name: userInfo.full_name,
        degree_prefix: userInfo.degree_prefix || null,
        degree_suffix: userInfo.degree_suffix || null,
        job: userInfo.job,
        location: userInfo.location,
        phone: userInfo.phone,
        summary: userInfo.summary,
        experience: userInfo.experience,
        education: userInfo.education,
        skills: userInfo.skills,
        photo: avatarUrl,
      })
      .eq('id', userInfo.id)

    if (error) {
      toast.error('Gagal menyimpan data profil')
      setLoading(false)
      return
    }

    // Save certificates
    try {
      // Save or update each certificate
      for (const cert of certificates) {
        // Skip empty certificates
        if (!cert.certificate_name || !cert.issuing_organization) continue

        const certData = {
          user_id: userInfo.id,
          certificate_name: cert.certificate_name,
          issuing_organization: cert.issuing_organization,
          issue_date: cert.issue_date || null,
          expiry_date: cert.expiry_date || null,
          credential_id: cert.credential_id || null,
          credential_url: cert.credential_url || null,
          description: cert.description || null,
        }

        if (cert.id) {
          // Update existing certificate
          const { error: updateError } = await supabase
            .from('certificates')
            .update(certData)
            .eq('id', cert.id)

          if (updateError) throw updateError
        } else {
          // Insert new certificate
          const { error: insertError } = await supabase
            .from('certificates')
            .insert([certData])

          if (insertError) throw insertError
        }
      }
    } catch (certError) {
      console.error('Error saving certificates:', certError)
      toast.error('Profil tersimpan, tapi gagal menyimpan sertifikat')
    }

    setLoading(false)
    toast.success('Profil dan sertifikat berhasil diperbarui')
    // Refresh user cache after successful update
    await refreshUserInfo()
    navigate('/dashboard')
  }

  if (userLoading || loadingData) return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-3xl mx-auto">
        <ProfileSkeleton />
      </div>
    </div>
  )

  if (!userInfo) return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
      <div className="px-4">
        <p className="text-center mt-10">Memuat data...</p>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-blue-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header Banner - Soft & Elegant */}
          <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-blue-50 rounded-t-2xl shadow-sm border border-slate-200/60 p-6 sm:p-8 relative overflow-hidden">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-200/10 rounded-full -mr-32 -mt-32"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-200/10 rounded-full -ml-24 -mb-24"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between flex-wrap gap-3 sm:gap-4">
                    <div className="flex items-center gap-2 sm:gap-4">
                      <button
                        onClick={() => navigate('/profile')}
                        className="bg-gradient-to-br from-blue-500/10 to-blue-500/10 backdrop-blur-sm text-blue-700 p-2 sm:p-2.5 rounded-lg sm:rounded-xl shadow-sm border border-blue-200/50 hover:border-blue-300 transition-all duration-200 group"
                        title="Back to Profile"
                      >
                        <ArrowLeft size={18} className="sm:w-5 sm:h-5 group-hover:-translate-x-1 transition-transform" />
                      </button>
                      <div>
                        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">Edit Profile</h1>
                        <p className="text-slate-600 text-xs sm:text-sm mt-1">Update informasi profil Anda</p>
                      </div>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <FileText size={20} className="sm:w-6 sm:h-6" />
                    </div>
                  </div>
                </div>
            </div>

            {/* Form Container */}
            <div className="bg-white rounded-b-2xl shadow-xl">
              <div className="p-6 sm:p-8">
                <form onSubmit={handleSubmit} className="space-y-8">

                  {/* Foto Profil Section */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-50 p-6 rounded-xl border border-blue-100">
                    <label className="block font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                        <Camera size={16} className="text-white" />
                      </div>
                      Foto Profil
                    </label>
                    <div className="flex items-center gap-6 flex-wrap">
                      {userInfo.photo && (
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full blur-md opacity-30"></div>
                          <img 
                            src={userInfo.photo} 
                            alt="Avatar" 
                            className="relative w-24 h-24 object-cover rounded-full border-4 border-white shadow-lg" 
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-[200px]">
                        <label className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-dashed border-blue-300 rounded-xl text-blue-700 hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer group">
                          <Upload size={18} className="group-hover:scale-110 transition-transform" />
                          <span className="font-medium">Upload Foto Baru</span>
                          <input 
                            type="file" 
                            accept="image/*" 
                            onChange={(e) => setAvatarFile(e.target.files[0])}
                            className="hidden"
                          />
                        </label>
                        {avatarFile && (
                          <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                            ✓ File dipilih: {avatarFile.name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Informasi Dasar */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 pb-2 border-b-2 border-blue-200">
                      <User size={20} className="text-blue-600" />
                      Informasi Dasar
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-semibold text-slate-700 mb-2">Nama Lengkap</label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                            <User size={18} />
                          </div>
                          <input 
                            name="full_name" 
                            value={userInfo.full_name} 
                            onChange={handleChange} 
                            className="w-full border border-slate-300 rounded-lg pl-11 pr-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                            placeholder="Masukkan nama lengkap"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block font-semibold text-slate-700 mb-2">Pekerjaan</label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                            <Briefcase size={18} />
                          </div>
                          <input 
                            name="job" 
                            value={userInfo.job} 
                            onChange={handleChange} 
                            className="w-full border border-slate-300 rounded-lg pl-11 pr-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                            placeholder="Posisi/jabatan"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Gelar Akademik */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-50 p-4 rounded-xl border border-blue-100">
                      <label className="block font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <GraduationCap size={18} className="text-blue-600" />
                        Gelar Akademik (Opsional)
                      </label>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-slate-600 mb-1">Gelar Depan</label>
                          <input 
                            name="degree_prefix" 
                            value={userInfo.degree_prefix || ''} 
                            onChange={handleChange} 
                            className="w-full border border-slate-300 rounded-lg px-4 py-2 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                            placeholder="Contoh: Dr., Prof."
                          />
                          <p className="text-xs text-slate-500 mt-1">Gelar sebelum nama</p>
                        </div>
                        <div>
                          <label className="block text-sm text-slate-600 mb-1">Gelar Belakang</label>
                          <input 
                            name="degree_suffix" 
                            value={userInfo.degree_suffix || ''} 
                            onChange={handleChange} 
                            className="w-full border border-slate-300 rounded-lg px-4 py-2 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                            placeholder="Contoh: S.Kom., M.T., Ph.D."
                          />
                          <p className="text-xs text-slate-500 mt-1">Gelar setelah nama</p>
                        </div>
                      </div>
                      <div className="mt-3 p-3 bg-white rounded-lg border border-blue-200">
                        <p className="text-sm text-slate-700 font-medium mb-1">Preview Nama Lengkap:</p>
                        <p className="text-lg text-slate-900 font-semibold">
                          {userInfo.degree_prefix && `${userInfo.degree_prefix} `}
                          {userInfo.full_name}
                          {userInfo.degree_suffix && `, ${userInfo.degree_suffix}`}
                        </p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-semibold text-slate-700 mb-2">Lokasi</label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                            <MapPin size={18} />
                          </div>
                          <input 
                            name="location" 
                            value={userInfo.location} 
                            onChange={handleChange} 
                            className="w-full border border-slate-300 rounded-lg pl-11 pr-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                            placeholder="Kota, Negara"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block font-semibold text-slate-700 mb-2">No. Telepon</label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                            <Phone size={18} />
                          </div>
                          <input 
                            name="phone" 
                            value={userInfo.phone} 
                            onChange={handleChange} 
                            className="w-full border border-slate-300 rounded-lg pl-11 pr-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                            placeholder="+62 xxx xxxx xxxx"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block font-semibold text-slate-700 mb-2">Tentang Saya</label>
                      <div className="relative">
                        <div className="absolute left-3 top-3 text-slate-400">
                          <FileText size={18} />
                        </div>
                        <textarea 
                          name="summary" 
                          value={userInfo.summary} 
                          onChange={handleChange} 
                          className="w-full border border-slate-300 rounded-lg pl-11 pr-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none resize-none" 
                          rows={4}
                          placeholder="Ceritakan tentang diri Anda..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Keahlian */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-50 p-6 rounded-xl border border-blue-100">
                    <label className="block font-bold text-slate-800 mb-2 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                        <Award size={16} className="text-white" />
                      </div>
                      Keahlian
                    </label>
                    <p className="text-sm text-slate-600 mb-3">Pisahkan dengan koma (,)</p>
                    <input 
                      type="text" 
                      value={userInfo.skills.join(', ')} 
                      onChange={handleSkillsChange} 
                      className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none bg-white"
                      placeholder="Contoh: JavaScript, React, Node.js, Python"
                    />
                    {userInfo.skills.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {userInfo.skills.map((skill, idx) => (
                          <span 
                            key={idx} 
                            className="bg-white text-blue-700 text-xs px-3 py-1.5 rounded-lg font-medium border border-blue-200 shadow-sm"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Pengalaman Kerja */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between pb-2 border-b-2 border-blue-200">
                      <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <Briefcase size={20} className="text-blue-600" />
                        Pengalaman Kerja
                      </h3>
                      <button 
                        type="button" 
                        onClick={addExperience} 
                        className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg "
                      >
                        <Plus size={16} />
                        Tambah
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      {userInfo.experience.map((exp, i) => (
                        <div key={i} className="bg-gradient-to-br from-blue-50 to-blue-50 p-5 rounded-xl border-2 border-blue-100 hover:border-blue-300 transition-all">
                          <div className="flex justify-between items-start mb-4">
                            <span className="bg-blue-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                              Pengalaman #{i + 1}
                            </span>
                            <button 
                              type="button" 
                              onClick={() => removeExperience(i)} 
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-all group"
                              title="Hapus"
                            >
                              <Trash2 size={18} className="group-hover:scale-110 transition-transform" />
                            </button>
                          </div>
                          
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-semibold text-slate-700 mb-1">Posisi</label>
                              <input 
                                placeholder="Contoh: Frontend Developer" 
                                value={exp.position} 
                                onChange={(e) => handleExperienceChange(i, 'position', e.target.value)} 
                                className="w-full border border-slate-300 bg-white px-4 py-2.5 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                              />
                            </div>
                            
                            <div className="grid md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1">
                                  <Building size={14} />
                                  Perusahaan
                                </label>
                                <input 
                                  placeholder="Nama perusahaan" 
                                  value={exp.company} 
                                  onChange={(e) => handleExperienceChange(i, 'company', e.target.value)} 
                                  className="w-full border border-slate-300 bg-white px-4 py-2.5 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1">
                                  <Calendar size={14} />
                                  Periode
                                </label>
                                <input 
                                  placeholder="Contoh: Jan 2020 - Des 2023" 
                                  value={exp.period} 
                                  onChange={(e) => handleExperienceChange(i, 'period', e.target.value)} 
                                  className="w-full border border-slate-300 bg-white px-4 py-2.5 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                                />
                              </div>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-semibold text-slate-700 mb-1">Deskripsi</label>
                              <textarea 
                                placeholder="Jelaskan tanggung jawab dan pencapaian Anda..." 
                                value={exp.description} 
                                onChange={(e) => handleExperienceChange(i, 'description', e.target.value)} 
                                className="w-full border border-slate-300 bg-white px-4 py-2.5 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none resize-none"
                                rows={3}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Pendidikan */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between pb-2 border-b-2 border-blue-200">
                      <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <GraduationCap size={20} className="text-blue-600" />
                        Pendidikan
                      </h3>
                      <button 
                        type="button" 
                        onClick={addEducation} 
                        className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-pink-700 transition-all shadow-md hover:shadow-lg "
                      >
                        <Plus size={16} />
                        Tambah
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      {userInfo.education.map((edu, i) => (
                        <div key={i} className="bg-gradient-to-br from-blue-50 to-pink-50 p-5 rounded-xl border-2 border-blue-100 hover:border-blue-300 transition-all">
                          <div className="flex justify-between items-start mb-4">
                            <span className="bg-blue-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                              Pendidikan #{i + 1}
                            </span>
                            <button 
                              type="button" 
                              onClick={() => removeEducation(i)} 
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-all group"
                              title="Hapus"
                            >
                              <Trash2 size={18} className="group-hover:scale-110 transition-transform" />
                            </button>
                          </div>
                          
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-semibold text-slate-700 mb-1">Institusi/Sekolah</label>
                              <input 
                                placeholder="Contoh: Universitas Indonesia" 
                                value={edu.school} 
                                onChange={(e) => handleEducationChange(i, 'school', e.target.value)} 
                                className="w-full border border-slate-300 bg-white px-4 py-2.5 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                              />
                            </div>
                            
                            <div className="grid md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Gelar/Jurusan</label>
                                <input 
                                  placeholder="Contoh: S1 Teknik Informatika" 
                                  value={edu.degree} 
                                  onChange={(e) => handleEducationChange(i, 'degree', e.target.value)} 
                                  className="w-full border border-slate-300 bg-white px-4 py-2.5 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1">
                                  <Calendar size={14} />
                                  Tahun
                                </label>
                                <input 
                                  placeholder="Contoh: 2020" 
                                  value={edu.year} 
                                  onChange={(e) => handleEducationChange(i, 'year', e.target.value)} 
                                  className="w-full border border-slate-300 bg-white px-4 py-2.5 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sertifikat */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between pb-2 border-b-2 border-green-200">
                      <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <Award size={20} className="text-green-600" />
                        Sertifikat
                      </h3>
                      <button 
                        type="button" 
                        onClick={addCertificate} 
                        className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg "
                      >
                        <Plus size={16} />
                        Tambah Sertifikat
                      </button>
                    </div>
                    
                    {certificatesLoading ? (
                      <div className="text-center py-4 text-slate-500">Loading...</div>
                    ) : (
                      <div className="space-y-4">
                        {certificates.map((cert, i) => (
                          <div key={i} className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl border-2 border-green-100 hover:border-green-300 transition-all">
                            <div className="flex justify-between items-start mb-4">
                              <span className="bg-green-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                                Sertifikat #{i + 1}
                              </span>
                              <button 
                                type="button" 
                                onClick={() => removeCertificate(i)} 
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-all group"
                                title="Hapus"
                              >
                                <Trash2 size={18} className="group-hover:scale-110 transition-transform" />
                              </button>
                            </div>
                            
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Nama Sertifikat *</label>
                                <input 
                                  placeholder="Contoh: AWS Certified Solutions Architect" 
                                  value={cert.certificate_name} 
                                  onChange={(e) => handleCertificateChange(i, 'certificate_name', e.target.value)} 
                                  className="w-full border border-slate-300 bg-white px-4 py-2.5 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1">
                                  <Building size={14} />
                                  Penerbit Sertifikat *
                                </label>
                                <input 
                                  placeholder="Contoh: Amazon Web Services (AWS)" 
                                  value={cert.issuing_organization} 
                                  onChange={(e) => handleCertificateChange(i, 'issuing_organization', e.target.value)} 
                                  className="w-full border border-slate-300 bg-white px-4 py-2.5 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                                />
                              </div>
                              
                              <div className="grid md:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1">
                                    <Calendar size={14} />
                                    Tanggal Terbit
                                  </label>
                                  <input 
                                    type="date"
                                    value={cert.issue_date || ''} 
                                    onChange={(e) => handleCertificateChange(i, 'issue_date', e.target.value)} 
                                    className="w-full border border-slate-300 bg-white px-4 py-2.5 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                                  />
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1">
                                    <Calendar size={14} />
                                    Tanggal Kadaluarsa
                                  </label>
                                  <input 
                                    type="date"
                                    value={cert.expiry_date || ''} 
                                    onChange={(e) => handleCertificateChange(i, 'expiry_date', e.target.value)} 
                                    placeholder="Kosongkan jika tidak ada"
                                    className="w-full border border-slate-300 bg-white px-4 py-2.5 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                                  />
                                </div>
                              </div>

                              <div className="grid md:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-semibold text-slate-700 mb-1">ID Kredensial</label>
                                  <input 
                                    placeholder="Contoh: ABC123XYZ"
                                    value={cert.credential_id || ''} 
                                    onChange={(e) => handleCertificateChange(i, 'credential_id', e.target.value)} 
                                    className="w-full border border-slate-300 bg-white px-4 py-2.5 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                                  />
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-semibold text-slate-700 mb-1">URL Kredensial</label>
                                  <input 
                                    type="url"
                                    placeholder="https://..." 
                                    value={cert.credential_url || ''} 
                                    onChange={(e) => handleCertificateChange(i, 'credential_url', e.target.value)} 
                                    className="w-full border border-slate-300 bg-white px-4 py-2.5 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Deskripsi</label>
                                <textarea 
                                  placeholder="Deskripsi tambahan tentang sertifikat ini..." 
                                  value={cert.description || ''} 
                                  onChange={(e) => handleCertificateChange(i, 'description', e.target.value)} 
                                  rows={2}
                                  className="w-full border border-slate-300 bg-white px-4 py-2.5 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none resize-none"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-6 border-t-2 border-gray-200">
                    <button 
                      type="button" 
                      onClick={() => navigate('/profile')} 
                      className="flex items-center justify-center gap-2 bg-gray-100 text-slate-700 px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-200 transition-all text-sm sm:text-base font-semibold border border-slate-300 hover:border-gray-300  order-2 sm:order-1"
                    >
                      <X size={16} className="sm:w-[18px] sm:h-[18px]" />
                      Batal
                    </button>
                    <button 
                      type="submit" 
                      disabled={loading} 
                      className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all text-sm sm:text-base font-semibold shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed  order-1 sm:order-2"
                    >
                      <Save size={16} className="sm:w-[18px] sm:h-[18px]" />
                      {loading ? 'Menyimpan...' : 'Simpan Perubahan'}
                    </button>
                  </div>

                </form>
              </div>
            </div>
          </motion.div>
        </div>
      <Footer />
    </div>
  )
}
